import React, { useEffect, useState } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminOverview, apiError } from "../../lib/adminApi";
import { Kpi, Panel, fmtMoney, ACCENT, btnGhost } from "./ui";
import ActivationDonut from "./ActivationDonut";
import { useAdminTheme } from "../../lib/adminTheme";

const tick = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { isLight } = useAdminTheme();

  const load = async () => {
    setBusy(true);
    try { setData(await adminOverview()); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-slate-500 text-[13px]">Carregando métricas…</div>;
  const { users, revenue, quotes, whatsapp, leads, series, activation } = data;
  const tooltipStyle = isLight
    ? { background: "#ffffff", border: "1px solid rgba(15,23,42,0.1)", borderRadius: 12, fontSize: 12, color: "#0B0F19" }
    : { background: "#0D1220", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, fontSize: 12 };
  const grid = isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.05)";

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Visão geral</h1>
          <p className="text-[13px] text-slate-400 mt-1">Saúde do produto nos últimos 30 dias.</p>
        </div>
        <button onClick={load} disabled={busy} className={btnGhost} data-testid="overview-refresh"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Atualizar</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Usuários" value={users.total} hint={`${users.new_7d} novos em 7d · ${users.new_30d} em 30d`} accent testid="kpi-users" />
        <Kpi label="Ativos (7d / 30d)" value={`${users.active_7d} / ${users.active_30d}`} hint="abriram o app" testid="kpi-active" />
        <Kpi label="Receita estimada (MRR)" value={fmtMoney(revenue.mrr)} hint={`${revenue.stripe_paying} pagantes Stripe · ${revenue.complimentary} cortesia`} testid="kpi-mrr" />
        <Kpi label="Orçamentos (30d)" value={quotes.last_30d} hint={`${quotes.web_30d} web · ${quotes.whatsapp_30d} WhatsApp · ${quotes.total} no total`} testid="kpi-quotes" />
        <Kpi label="Planos" value={`${users.by_plan.pro} Pro`} hint={`${users.by_plan.standard} Standard · ${users.by_plan.free} Free`} testid="kpi-plans" />
        <Kpi label="WhatsApp" value={whatsapp.messages_30d} hint={`mensagens 30d · ${whatsapp.pdfs_30d} PDFs · ${whatsapp.linked_numbers} números ligados`} testid="kpi-whatsapp" />
        <Kpi label="Leads" value={leads.whatsapp + leads.free_without_quote} hint={`${leads.whatsapp} WhatsApp sem conta · ${leads.free_without_quote} Free sem orçamento`} testid="kpi-leads" />
        <Kpi label="Bloqueados" value={users.disabled} hint="contas desativadas" testid="kpi-disabled" />
      </div>

      <div className="grid lg:grid-cols-2 xl:grid-cols-[360px_1fr_1fr] gap-4">
        <ActivationDonut data={activation} isLight={isLight} />
        <Panel title="Orçamentos por dia" testid="chart-quotes">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ left: -20, right: 0, top: 4 }}>
                <CartesianGrid vertical={false} stroke={grid} />
                <XAxis dataKey="day" tickFormatter={tick} tick={{ fill: "#64748b", fontSize: 10 }} interval={4} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={tick} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="quotes_web" name="Web" stackId="q" fill={ACCENT} radius={[0, 0, 0, 0]} />
                <Bar dataKey="quotes_wa" name="WhatsApp" stackId="q" fill="#25D366" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Uso: ativos, mensagens WhatsApp e cadastros" testid="chart-usage">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ left: -20, right: 0, top: 4 }}>
                <defs>
                  <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={ACCENT} stopOpacity={0.5} /><stop offset="100%" stopColor={ACCENT} stopOpacity={0} /></linearGradient>
                  <linearGradient id="gW" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#25D366" stopOpacity={0.5} /><stop offset="100%" stopColor="#25D366" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={grid} />
                <XAxis dataKey="day" tickFormatter={tick} tick={{ fill: "#64748b", fontSize: 10 }} interval={4} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={tick} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="active" name="Usuários ativos" stroke={ACCENT} fill="url(#gA)" strokeWidth={2} />
                <Area type="monotone" dataKey="wa_msgs" name="Msgs WhatsApp" stroke="#25D366" fill="url(#gW)" strokeWidth={2} />
                <Area type="monotone" dataKey="signups" name="Cadastros" stroke="#f59e0b" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}
